import type { MatchTeamKit, TournamentStageKey, WorldCupMatch } from '../types/worldcup.types';
import type { FuturesPick } from '../hooks/useWorldCupPicksData';
import { formatMatchStageBadgeLabel } from './worldcup.helper';

// ---------------------------------------------------------------------------
// Stage key mappings
// ---------------------------------------------------------------------------

export const BACKEND_TO_FRONTEND_STAGE: Record<string, (round?: string | null) => TournamentStageKey> = {
  GROUP_STAGE: (round) => {
    if (round === '3') return 'third_round';
    if (round === '2') return 'second_round';
    return 'first_round';
  },
  ROUND_OF_32: () => 'round_of_32',
  ROUND_OF_16: () => 'round_of_16',
  QUARTER_FINALS: () => 'quarter_finals',
  SEMI_FINALS: () => 'semi_finals',
  THIRD_PLACE_MATCH: () => 'third_place',
  FINAL: () => 'final',
  REGULAR_SEASON: () => 'regular_season',
};

export const FRONTEND_TO_BACKEND_STAGE: Record<TournamentStageKey, string> = {
  first_round: 'GROUP_STAGE',
  second_round: 'GROUP_STAGE',
  third_round: 'GROUP_STAGE',
  round_of_32: 'ROUND_OF_32',
  round_of_16: 'ROUND_OF_16',
  quarter_finals: 'QUARTER_FINALS',
  semi_finals: 'SEMI_FINALS',
  third_place: 'THIRD_PLACE_MATCH',
  final: 'FINAL',
  regular_season: 'REGULAR_SEASON',
};

export const GROUP_STAGE_FRONTEND_KEYS: TournamentStageKey[] = ['first_round', 'second_round', 'third_round'];

/**
 * UI stages where a Golden Ball (x2) may be placed. Mirrors the server's
 * GOLDEN_BALL_ELIGIBLE_STAGES (GROUP_STAGE + ROUND_OF_32): the boost is only allowed through the
 * Round of 32. From the Round of 16 onward the server rejects the PUT with 400, so the button must
 * render greyed/disabled rather than looking tappable and snapping back.
 */
export const GOLDEN_BALL_ELIGIBLE_FRONTEND_STAGES: ReadonlySet<TournamentStageKey> = new Set<TournamentStageKey>([
  'first_round',
  'second_round',
  'third_round',
  'round_of_32',
]);

/** True when the Golden Ball (x2) may be placed on a match in this UI stage. */
export function isGoldenBallEligibleStage(stageKey: TournamentStageKey): boolean {
  return GOLDEN_BALL_ELIGIBLE_FRONTEND_STAGES.has(stageKey);
}

/**
 * Round slot each UI stage maps to for the Golden Ball PUT. Group Stage holds three independent
 * boosts (rounds "1"/"2"/"3"); single-round stages use the "1" sentinel. Sent so the server keys
 * the right per-round row even on clear (where there is no target match to derive the round from).
 */
export const FRONTEND_TO_GOLDEN_BALL_ROUND: Record<TournamentStageKey, string> = {
  first_round: '1',
  second_round: '2',
  third_round: '3',
  round_of_32: '1',
  round_of_16: '1',
  quarter_finals: '1',
  semi_finals: '1',
  third_place: '1',
  final: '1',
  // Season matchweeks never carry a Golden Ball (server v1 cut) — sentinel for map exhaustiveness.
  regular_season: '1',
};

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------

export interface WorldCupFuturesParticipantTeam {
  id: string;
  name: string;
  countryCode: string;
  odd?: number;
}

export interface WorldCupFuturesTopScorerOption {
  id: string;
  label: string;
  countryCode?: string;
  number?: number;
  kitColor?: string;
  trimColor?: string;
  odd?: number;
}

export interface WorldCupFuturesStats {
  winnerPcts: Record<string, number>;
  scorerPcts: Record<string, number>;
}

export interface WorldCupFuturesOptions {
  participantTeams: WorldCupFuturesParticipantTeam[];
  topScorers?: WorldCupFuturesTopScorerOption[];
  topScorerOtherId?: string;
}

export interface ApiMatch {
  competitionMatchId: string;
  matchId: string;
  homeTeamId: string;
  awayTeamId: string;
  homeTeam: string;
  awayTeam: string;
  homeCountryCode: string;
  awayCountryCode: string;
  /** Per-club kit (AC-3.8); absent from a server that predates the field. */
  homeKit?: MatchTeamKit | null;
  awayKit?: MatchTeamKit | null;
  kickoffTime: string;
  status: 'upcoming' | 'live' | 'finished';
  stage: string;
  stageLabel: string;
  round: string | null;
  displayOrder: number;
  homeScore: number | null;
  awayScore: number | null;
  fullTimeHomeScore?: number | null;
  fullTimeAwayScore?: number | null;
  penaltyHomeScore?: number | null;
  penaltyAwayScore?: number | null;
  liveMinute: number | null;
  isPickEditable?: boolean;
  prediction: {
    home: number;
    away: number;
    isMonkeyPick?: boolean;
    pointsEarned: number | null;
  } | null;
  hasGoldenBall: boolean;
  isGoldenBallLocked: boolean;
}

/** Shape of `response.data` from GET /competitions/:id/matches */
export interface CompetitionMatchesApiPayload {
  success: boolean;
  error?: string;
  data: {
    matches: ApiMatch[];
    futuresOptions?: WorldCupFuturesOptions;
    goldenBalls?: Record<string, { competitionMatchId: string | null; locked: boolean }>;
    /** Per-round Golden Ball assignments keyed by competitionMatchId (Group Stage = up to 3). */
    goldenBallsByMatch?: Record<string, { competitionMatchId: string; locked: boolean }>;
    futures?: FuturesPick | null;
    futuresEditable?: boolean;
  };
}

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

export function mapApiMatch(m: ApiMatch): WorldCupMatch {
  const stageMapper = BACKEND_TO_FRONTEND_STAGE[m.stage];
  const stageKey = stageMapper ? stageMapper(m.round) : ('first_round' as TournamentStageKey);

  return {
    id: m.matchId,
    homeTeamId: m.homeTeamId,
    awayTeamId: m.awayTeamId,
    homeTeam: m.homeTeam,
    awayTeam: m.awayTeam,
    homeCountryCode: m.homeCountryCode,
    awayCountryCode: m.awayCountryCode,
    homeKit: m.homeKit ?? null,
    awayKit: m.awayKit ?? null,
    kickoffTime: m.kickoffTime,
    status: m.status,
    round: m.round || '1',
    stageKey,
    stageLabel: formatMatchStageBadgeLabel({
      stageLabel: m.stageLabel,
      round: m.round == null ? '' : String(m.round),
    }),
    homeScore: m.homeScore ?? undefined,
    awayScore: m.awayScore ?? undefined,
    fullTimeHomeScore: m.fullTimeHomeScore ?? undefined,
    fullTimeAwayScore: m.fullTimeAwayScore ?? undefined,
    penaltyHomeScore: m.penaltyHomeScore ?? undefined,
    penaltyAwayScore: m.penaltyAwayScore ?? undefined,
    isPickEditable: m.isPickEditable,
    liveMinute: m.liveMinute ?? undefined,
    prediction: m.prediction
      ? {
          home: m.prediction.home,
          away: m.prediction.away,
          isMonkeyPick: m.prediction.isMonkeyPick,
          pointsEarned: m.prediction.pointsEarned ?? undefined,
        }
      : undefined,
  };
}

/**
 * Each round now owns an independent Golden Ball, so a toggle only touches its own stage key —
 * placing round 2 no longer clears round 1 (the old one-row-per-GROUP_STAGE collapse is gone).
 */
export function applyBoostOptimisticUpdate(
  prev: Partial<Record<TournamentStageKey, string>>,
  stageKey: TournamentStageKey,
  newBoostMatchId: string | undefined,
): Partial<Record<TournamentStageKey, string>> {
  const next = { ...prev };
  if (!newBoostMatchId) {
    delete next[stageKey];
  } else {
    next[stageKey] = newBoostMatchId;
  }
  return next;
}

/** Lock tournament winner + top scorer once the earliest scheduled match has kicked off. */
export function areWorldCupFuturesLockedByFirstKickoff(
  matches: readonly { kickoffTime: string }[],
): boolean {
  if (matches.length === 0) return false;
  const now = Date.now();
  let minKick = Infinity;
  for (const m of matches) {
    const t = new Date(m.kickoffTime).getTime();
    if (Number.isFinite(t) && t < minKick) minKick = t;
  }
  return minKick !== Infinity && minKick <= now;
}

/**
 * Drop futures ids not present in current GET /matches `futuresOptions` before PUT
 * (stale DB / renamed allowlist entries).
 */
export function sanitizeWorldCupFuturesPayloadForSave(
  winnerTeamId: string | null,
  topScorerPlayerId: string | null,
  futuresOptions: WorldCupFuturesOptions | null,
): { winnerTeamId: string | null; topScorerPlayerId: string | null } {
  if (!futuresOptions) {
    return { winnerTeamId, topScorerPlayerId };
  }

  const teamAllowed =
    winnerTeamId == null || futuresOptions.participantTeams.some((t) => t.id === winnerTeamId);
  const scorerAllowed =
    topScorerPlayerId == null
    || (futuresOptions.topScorerOtherId != null && topScorerPlayerId === futuresOptions.topScorerOtherId)
    || Boolean(futuresOptions.topScorers?.some((o) => o.id === topScorerPlayerId));

  return {
    winnerTeamId: teamAllowed ? winnerTeamId : null,
    topScorerPlayerId: scorerAllowed ? topScorerPlayerId : null,
  };
}
